import { useState } from "react";
import { Mail, MapPin, MessageCircle, CheckCircle2, Loader2, Info } from "lucide-react";
import { toast } from "sonner";
import { api, waLink, formatApiError } from "../lib/api";
import { useSite } from "../lib/SiteContext";
import { useEnquiry } from "../lib/EnquiryContext";
import Seo from "../components/Seo";
import FadeIn from "../components/FadeIn";

const inputCls = "w-full rounded-lg border border-input bg-white px-3.5 py-2.5 text-sm outline-none transition-colors focus:border-pine focus:ring-2 focus:ring-pine/15";
const labelCls = "mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-500";

const Contact = () => {
  const { content } = useSite();
  const { openEnquiry } = useEnquiry();
  const settings = content?.settings;
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    const errs = {};
    if (!form.name.trim()) errs.name = "Please tell us your name.";
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)) errs.email = "Please enter a valid email address.";
    if (!form.message.trim()) errs.message = "Please write a short message.";
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setSubmitting(true);
    try {
      await api.post("/contact", form);
      setSent(true);
      toast.success("Message sent — we will reply soon.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <Seo
        title="Contact Us — Aloha Corfu Tours | Corfu Private Tours"
        description="Contact Aloha Corfu Tours for private tour enquiries in Corfu. Reach us by contact form, email or WhatsApp at +30 698 340 0353."
      />
      <section className="bg-pine-dark py-14 md:py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <FadeIn>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-seafoam">We reply personally</p>
            <h1 className="mt-2 font-display text-4xl font-semibold text-white sm:text-5xl">Contact Us</h1>
            <p className="mt-4 max-w-2xl text-base text-white/75">
              Questions about a tour, a date or a special occasion? Send a message — we answer every enquiry personally.
            </p>
          </FadeIn>
        </div>
      </section>

      <section className="py-12 md:py-16">
        <div className="mx-auto grid max-w-7xl gap-10 px-4 sm:px-6 lg:grid-cols-5 lg:px-8">
          <div className="space-y-4 lg:col-span-2">
            <FadeIn>
              <a href={waLink(settings)} target="_blank" rel="noopener noreferrer"
                 className="flex items-center gap-4 rounded-2xl border border-gray-100 bg-white p-5 shadow-soft transition-shadow hover:shadow-lift" data-testid="contact-whatsapp-card">
                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-[#25D366]/10">
                  <MessageCircle className="h-6 w-6 text-[#25D366]" />
                </span>
                <span>
                  <span className="block font-semibold text-pine">WhatsApp</span>
                  <span className="block text-sm text-gray-500">+30 698 340 0353 — fastest reply</span>
                </span>
              </a>
            </FadeIn>
            {settings?.contact_email && (
              <FadeIn delay={0.05}>
                <a href={`mailto:${settings.contact_email}`}
                   className="flex items-center gap-4 rounded-2xl border border-gray-100 bg-white p-5 shadow-soft transition-shadow hover:shadow-lift" data-testid="contact-email-card">
                  <span className="flex h-12 w-12 items-center justify-center rounded-full bg-seafoam-light">
                    <Mail className="h-6 w-6 text-pine" />
                  </span>
                  <span>
                    <span className="block font-semibold text-pine">Email</span>
                    <span className="block text-sm text-gray-500">{settings.contact_email}</span>
                  </span>
                </a>
              </FadeIn>
            )}
            <FadeIn delay={0.1}>
              <div className="flex items-center gap-4 rounded-2xl border border-gray-100 bg-white p-5 shadow-soft" data-testid="contact-location-card">
                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-seafoam-light">
                  <MapPin className="h-6 w-6 text-pine" />
                </span>
                <span>
                  <span className="block font-semibold text-pine">Corfu, Greece</span>
                  <span className="block text-sm text-gray-500">Pickup from hotels, villas & the port</span>
                </span>
              </div>
            </FadeIn>
            <FadeIn delay={0.15}>
              <div className="rounded-2xl bg-sand p-5" data-testid="contact-info-card">
                <p className="flex items-center gap-2 text-sm font-semibold text-pine">
                  <Info className="h-4 w-4 text-terracotta" /> About tour enquiries
                </p>
                <p className="mt-2 text-sm leading-relaxed text-gray-600">
                  All bookings begin as enquiries — no payment is taken online. Tell us your tour, date and group size,
                  and we confirm availability and a quote personally. For a ready-made enquiry with dates and details, use our{" "}
                  <button onClick={() => openEnquiry()} className="font-semibold text-pine underline underline-offset-2" data-testid="contact-open-enquiry">
                    tour enquiry form
                  </button>.
                </p>
              </div>
            </FadeIn>
          </div>

          <FadeIn delay={0.1} className="lg:col-span-3">
            <div className="rounded-2xl border border-gray-100 bg-white p-6 shadow-lift sm:p-8" data-testid="contact-form-card">
              {sent ? (
                <div className="flex flex-col items-center py-10 text-center" data-testid="contact-success">
                  <CheckCircle2 className="h-14 w-14 text-pine" />
                  <h2 className="mt-4 font-display text-2xl font-semibold text-pine">Message received!</h2>
                  <p className="mt-2 max-w-sm text-sm text-gray-500">Thank you for reaching out. We will reply to your email personally, usually the same day.</p>
                </div>
              ) : (
                <form onSubmit={submit} noValidate className="grid grid-cols-1 gap-4 sm:grid-cols-2" data-testid="contact-form">
                  <h2 className="font-display text-2xl font-semibold text-pine sm:col-span-2">Send us a message</h2>
                  <div>
                    <label className={labelCls} htmlFor="contact-name">Your name *</label>
                    <input id="contact-name" className={inputCls} value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="contact-name-input" />
                    {errors.name && <p className="mt-1 text-xs text-red-600" data-testid="contact-error-name">{errors.name}</p>}
                  </div>
                  <div>
                    <label className={labelCls} htmlFor="contact-email">Email address *</label>
                    <input id="contact-email" type="email" className={inputCls} value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="contact-email-input" />
                    {errors.email && <p className="mt-1 text-xs text-red-600" data-testid="contact-error-email">{errors.email}</p>}
                  </div>
                  <div className="sm:col-span-2">
                    <label className={labelCls} htmlFor="contact-subject">Subject</label>
                    <input id="contact-subject" className={inputCls} placeholder="e.g. Question about the Paleokastritsa tour"
                           value={form.subject} onChange={(e) => set("subject", e.target.value)} data-testid="contact-subject-input" />
                  </div>
                  <div className="sm:col-span-2">
                    <label className={labelCls} htmlFor="contact-message">Message *</label>
                    <textarea id="contact-message" rows={5} className={inputCls} value={form.message} onChange={(e) => set("message", e.target.value)} data-testid="contact-message-input" />
                    {errors.message && <p className="mt-1 text-xs text-red-600" data-testid="contact-error-message">{errors.message}</p>}
                  </div>
                  <div className="sm:col-span-2">
                    <button type="submit" disabled={submitting} data-testid="contact-submit-button"
                            className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-pine px-6 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-pine-light disabled:opacity-60 sm:w-auto">
                      {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
                      {submitting ? "Sending..." : "Send Message"}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </FadeIn>
        </div>
      </section>
    </div>
  );
};

export default Contact;
